import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Hourglass, Play, X } from "lucide-react";
import { approveRecoveryRequest, cancelRecoveryRequest, completeRecoveryRequest, fetchRecoveryRequests } from "../../api/recovery.js";
import type { RecoveryRequest } from "../../api/recovery.js";
import { describeAuthError } from "../../app/authFlow.js";
import { ADMIN_AUDIT_KEY, ADMIN_USERS_KEY } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

const REQUESTS_KEY: ReadonlyArray<string> = ["admin", "recovery", "requests"];

type Act = "approve" | "complete" | "cancel";

function describe(request: RecoveryRequest): string {
  if (request.resetPassword && request.resetTwoFactor) {
    return "Neues Master-Passwort und 2FA zurücksetzen";
  }
  return request.resetPassword ? "Neues Master-Passwort" : "2FA zurücksetzen";
}

/** Admin: open two-person recovery requests – approve, carry out after the hold, or cancel. */
export function RecoveryRequestsPanel(props: { readonly selfId: string }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const requests = useQuery({ queryKey: REQUESTS_KEY, queryFn: fetchRecoveryRequests });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(request: RecoveryRequest, kind: Act): Promise<void> {
    setBusy(`${request.id}:${kind}`);
    setError(null);
    try {
      if (kind === "approve") {
        await approveRecoveryRequest(request.id);
        toast.notify("Anfrage freigegeben.", { tone: "success" });
      } else if (kind === "complete") {
        await completeRecoveryRequest(request.id);
        toast.notify(`Konto ${request.email ?? ""} wiederhergestellt.`, { tone: "success" });
      } else {
        await cancelRecoveryRequest(request.id);
        toast.notify("Anfrage abgebrochen.", { tone: "success" });
      }
      void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader
        icon={Hourglass}
        title="Offene Anfragen"
        description="Jede Wiederherstellung braucht die Zustimmung eines zweiten Administrators und läuft erst nach der Wartezeit. Der Kontoinhaber kann sie bis dahin per E-Mail-Link abbrechen."
      />
      <div className="flex flex-col gap-3 px-5 py-4">
        {error !== null ? <Alert tone="danger">{error}</Alert> : null}
        {requests.isPending ? <Skeleton className="h-20" /> : null}
        {requests.error !== null ? <Alert tone="danger">{requests.error.message}</Alert> : null}
        {requests.data !== undefined && requests.data.length === 0 ? <EmptyState icon={Hourglass} title="Keine offenen Anfragen" /> : null}
        {requests.data !== undefined && requests.data.length > 0 ? (
          <ul className="divide-y divide-line">
            {requests.data.map((request: RecoveryRequest): ReactNode => {
              const mine = request.requestedBy === props.selfId;
              const approvedByMe = request.approvals.some((a): boolean => a.adminId === props.selfId);
              return (
                <li key={request.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 text-sm">
                    <p className="truncate font-medium text-foam">{request.email ?? request.userId}</p>
                    <p className="text-xs text-mist-2">
                      {describe(request)} · von {request.requestedByEmail ?? "unbekannt"} · {request.approvals.length}/{request.requiredApprovals} Zustimmungen
                    </p>
                    <p className="text-xs text-mist-2" title={formatDateTime(request.executeAfter)}>
                      {request.ready ? <Badge tone="mint">Bereit</Badge> : `Frühestens ${formatRelativeTime(request.executeAfter, Date.now())}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!mine && !approvedByMe && !request.approved ? (
                      <Button size="sm" variant="secondary" icon={Check} loading={busy === `${request.id}:approve`} onClick={(): void => void act(request, "approve")}>
                        Zustimmen
                      </Button>
                    ) : null}
                    {request.ready ? (
                      <Button size="sm" icon={Play} loading={busy === `${request.id}:complete`} onClick={(): void => void act(request, "complete")}>
                        Ausführen
                      </Button>
                    ) : null}
                    <Button size="sm" variant="ghost" icon={X} loading={busy === `${request.id}:cancel`} onClick={(): void => void act(request, "cancel")}>
                      Abbrechen
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
